import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate, useParams } from 'react-router-dom';
import Clarity from '@microsoft/clarity';
import { useClarityTracking } from './hooks/useClarityTracking';
import Layout from './components/Layout';
import ProgressSteps from './components/ProgressSteps';
import LandingScreen from './features/landing/LandingScreen';
import ExistingAudiences from './features/existingAudience/ExistingAudiences';
import UseCaseSelector from './features/simulationUseCase/UseCaseSelector';
import SimulationResultsContent from './features/simulationResults/SimulationResultsContent';
import PublicSimulationView from './features/simulationResults/PublicSimulationView';
import ChatPage from './features/chat/ChatPage';
import AdminPanel from './features/admin/AdminPanel';
import AnalysisPage from './features/analysis/AnalysisPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AudienceProvider, useAudience } from './context/AudienceContext';
import { BuildAudienceProvider, useBuildAudience } from './context/BuildAudienceContext';
import { AudienceDataProvider } from './contexts/AudienceContext';
import { SubscriptionProvider } from './context/subscription/SubscriptionContext';
import ErrorBoundary from './components/ErrorBoundary';
import Auth from './features/auth/Auth';
import PasswordReset from './features/auth/PasswordReset';
import ProtectedRoute from './components/ProtectedRoute';
import Button from './components/Button';
import { ArrowLeft } from 'lucide-react';
import ChatWithPersona from './features/chatWithPersonas/ChatWithPersona';
import AskCollectiveLanding from './features/digitalTwin/AskCollectiveLanding';
import AskCollectiveChat from './features/digitalTwin/AskCollectiveChat';
import AskHealthcareChat from './features/digitalTwin/AskHealthcareChat';
import AskNaturalOrganicChat from './features/digitalTwin/AskNaturalOrganicChat';
import HealthcareChat from './features/healthcareChat/HealthcareChat';
import AskRedditPersonasChat from './features/digitalTwin/AskRedditPersonasChat';
import MaintenanceMode from './components/MaintenanceMode';
import AudienceDefine from './features/buildAudience/AudienceDefine';
import AudienceSegmentsFocus from './features/buildAudience/AudienceSegmentsFocus';
import AudienceSegmentationApproach from './features/buildAudience/AudienceSegmentationApproach';
import AudienceFinalize from './features/buildAudience/AudienceFinalize';
import PricingPage from './features/subscription/PricingPage';
import SubscriptionSuccess from './features/subscription/SubscriptionSuccess';
import SubscriptionCancelled from './features/subscription/SubscriptionCancelled';
import { checkResetAuthorization } from './services/adminService';
import SurveyBuilder from './features/aiSurvey/SurveyBuilder';
import SurveysList from './features/aiSurvey/SurveysList';
import SurveySession from './features/aiSurvey/SurveySession';
import SurveyDetails from './features/aiSurvey/SurveyDetails';
import SurveyAnalysisPage from './features/aiSurvey/SurveyAnalysisPage';
import SurveyComplete from './features/aiSurvey/SurveyComplete';
import SurveyResponses from './features/aiSurvey/SurveyResponses';
import SessionResponses from './features/aiSurvey/SessionResponses';
import SurveyGroupDetails from './features/aiSurvey/SurveyGroupDetails';
import { PrivacyPolicy, TermsOfService, AcceptableUsePolicy } from './features/legal';
type Step =
  | 'landing'
  | 'existing-audiences'
  | 'simulation-use-case'
  | 'simulation-results'
  | 'audience-define'
  | 'audience-questionnaire'
  | 'audience-segments'
  | 'audience-final';

type FlowStep = 'segment-selection' | 'use-case-selection' | 'form-filling' | 'results-view';

// Main application content component
const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { audienceData, updateAudienceData } = useAudience();
  const { currentStep: buildCurrentStep, setCurrentStep: setBuildCurrentStep, resetBuildAudienceData } = useBuildAudience();
  const [stepMapping, setStepMapping] = useState<Step>('landing');
  const [flowType, setFlowType] = useState<'build' | 'existing' | null>(null);
  const [selectedAudienceId, setSelectedAudienceId] = useState<number | null>(null);
  const [selectedAudienceName, setSelectedAudienceName] = useState<string>('');
  const [currentStepInUseCase, setCurrentStepInUseCase] = useState<FlowStep>('segment-selection');
  const [hasEditedStep, setHasEditedStep] = useState<boolean>(false); // Track if user has edited a step
  const [formState, setFormState] = useState<Record<string, any>>({}); // Track form data
  const [visitedSteps, setVisitedSteps] = useState<Set<Step>>(new Set(['landing']));
  const [visitedUseCaseSteps, setVisitedUseCaseSteps] = useState<Set<FlowStep>>(new Set());
  const [currentSimulationId, setCurrentSimulationId] = useState<number | null>(null); // Add state to store the simulation ID that was generated
  
  // Initialize Clarity tracking for page views
  const { identifyUser, setTag, trackEvent } = useClarityTracking();

  // Identify user with Clarity when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Identify user with hashed ID for privacy
      identifyUser(user.id.toString(), user.email);

      // Set user type tag
      setTag('user_type', user.is_admin ? 'admin' : 'regular');

      // Set authentication status tag
      setTag('auth_status', 'authenticated');
    } else {
      // Set unauthenticated tag
      setTag('auth_status', 'unauthenticated');
    }
  }, [isAuthenticated, user, identifyUser, setTag]);

  // Map numeric currentStep to string step type
  const updateStepMapping = useCallback((step: number) => {
    switch (step) {
      // Build audience flow steps - simplified 3-step flow
      case 101:
        setStepMapping('audience-define');
        break;
      case 102:
        setStepMapping('audience-questionnaire');
        break;
      case 103:
        setStepMapping('audience-segments');
        break;
      case 104:
        setStepMapping('audience-final');
        break;
      default:
        setStepMapping('landing');
    }
  }, []);
  
  const handleBuildAudience = useCallback(() => {
    setFlowType('build');
    setBuildCurrentStep(101); // Set to first step - Define Simulation Target
    updateStepMapping(101);
    navigate('/build-audience');
  }, [setFlowType, setBuildCurrentStep, updateStepMapping, navigate]);
  
  const handleSelectExisting = useCallback(() => {
    setFlowType('existing');
    setStepMapping('existing-audiences');
    navigate('/simulate');
  }, [setFlowType, setStepMapping, navigate]);
  
  const handleNext = () => {
    const nextStep = buildCurrentStep + 1;
    setBuildCurrentStep(nextStep);
    updateStepMapping(nextStep);
  };
  
  const handleBack = () => {
    if (buildCurrentStep > 101) {
      const prevStep = buildCurrentStep - 1;
      setBuildCurrentStep(prevStep);
      updateStepMapping(prevStep);
    } else {
      // Go back to landing
      setStepMapping('landing');
      setFlowType(null);
    }
  };
  
  const handleReset = () => {
    setStepMapping('landing');
    setBuildCurrentStep(101);
    setFlowType(null);
    // Reset both contexts
    updateAudienceData({
      type: null,
      websiteUrl: '',
      segmentType: null,
      specificSegment: '',
      qualitativeInfo: '',
      uploadedFile: null,
      audienceId: null,
      audienceName: '',
      selectedUseCase: null,
      selectedSegments: [],
      personaFilters: {},
    });
    resetBuildAudienceData();
  };
  
  // Check URL path and set appropriate step on component mount or route change
  useEffect(() => {
    // Reset use case state when navigating away
    const resetUseCaseState = () => {
      setCurrentStepInUseCase('segment-selection');
      setHasEditedStep(false);
      setFormState({});
    };

    if (location.pathname === '/build-audience') {
      setFlowType('build');
      setBuildCurrentStep(101);
      updateStepMapping(101);
      resetUseCaseState();
    } else if (location.pathname === '/simulate') {
      setFlowType('existing');
      setStepMapping('existing-audiences');
      resetUseCaseState();
    } else if (location.pathname === '/') {
      setStepMapping('landing');
      setBuildCurrentStep(101);
      setFlowType(null);
      resetUseCaseState();
      updateAudienceData({
        type: null,
        websiteUrl: '',
        segmentType: null,
        specificSegment: '',
        qualitativeInfo: '',
        uploadedFile: null,
        audienceId: null,
        audienceName: '',
        selectedUseCase: null,
        selectedSegments: [],
        personaFilters: {},
      });
      resetBuildAudienceData();
    } else if (location.pathname.startsWith('/analysis') || 
               location.pathname === '/chat-with-persona' ||
               location.pathname === '/chat' ||
               location.pathname === '/admin' ||
               location.pathname.startsWith('/subscription')) {
      // For any other navigation, reset the simulation flow
      setStepMapping('landing');
      resetUseCaseState();
      setFlowType(null);
    }
  }, [location.pathname]);

  const handleSelectAudience = (audienceId: number, audienceName: string) => {
    setSelectedAudienceId(audienceId);
    setSelectedAudienceName(audienceName);
    // Skip segment selection step and go directly to use case
    updateAudienceData({ 
      audienceId: audienceId,
      audienceName: audienceName,
      selectedSegments: [], // Initialize with empty segments
      personaFilters: {}    // Initialize with empty filters
    });
    setStepMapping('simulation-use-case');
    setCurrentStepInUseCase('segment-selection'); // Always go to step 2 after selection
    // Reset edit tracking when selecting a new audience
    setHasEditedStep(false);
    // Mark this step as visited
    setVisitedSteps(prev => new Set([...prev, 'existing-audiences', 'simulation-use-case']));
  };
  
  const handleFormDataChange = (useCaseType: string, data: Record<string, any>) => {
    const key = useCaseType.toLowerCase();

    // Check if data has actually changed before updating state
    if (JSON.stringify(formState[key]) === JSON.stringify(data)) {
      return;
    }

    setFormState(prev => ({
      ...prev,
      [key]: data
    }));
  };

  const hasVisitedStep = (step: Step): boolean => {
    return visitedSteps.has(step);
  };

  const hasVisitedUseCaseStep = (step: FlowStep): boolean => {
    return visitedUseCaseSteps.has(step);
  };

  const handleUseCaseStepChange = (newStep: FlowStep) => {
    setCurrentStepInUseCase(newStep);
    
    // Mark this step as visited
    setVisitedUseCaseSteps(prev => new Set([...prev, newStep]));
    
    // If moving forward in the flow naturally, reset the edit flag
    if (
      (currentStepInUseCase === 'segment-selection' && newStep === 'use-case-selection') ||
      (currentStepInUseCase === 'use-case-selection' && newStep === 'form-filling') ||
      (currentStepInUseCase === 'form-filling' && newStep === 'results-view')
    ) {
      setHasEditedStep(false);
    }
  };

  const getProgressSteps = () => {
    if (flowType === 'build') {
      return [
        {
          label: '1. Define Target',
          completed: buildCurrentStep > 101,
          current: buildCurrentStep === 101,
          onClick: buildCurrentStep > 101 ? () => {
            setBuildCurrentStep(101);
            updateStepMapping(101);
          } : undefined
        },
        {
          label: '2. Define Focus',
          completed: buildCurrentStep > 102,
          current: buildCurrentStep === 102,
          onClick: buildCurrentStep > 102 ? () => {
            setBuildCurrentStep(102);
            updateStepMapping(102);
          } : undefined
        },
        {
          label: '3. Define Approach & Build',
          completed: buildCurrentStep > 103,
          current: buildCurrentStep === 103 || buildCurrentStep === 104,
          onClick: buildCurrentStep > 103 ? () => {
            setBuildCurrentStep(103);
            updateStepMapping(103);
          } : undefined
        }
      ];
    } else if (flowType === 'existing') {
      // Expanded 5-step flow for better granularity
      return [
        { 
          label: selectedAudienceName ? `1. Audience: ${selectedAudienceName}` : '1. Select Audience', 
          completed: hasVisitedStep('simulation-use-case') || stepMapping === 'simulation-results', 
          current: stepMapping === 'existing-audiences',
          // Always allow going back to audience selection
          onClick: () => {
            setStepMapping('existing-audiences');
            setHasEditedStep(false); // Reset edits when going back to first step
          }
        },
        { 
          label: '2. Select Segments',
          completed: hasVisitedUseCaseStep('use-case-selection') || 
                     hasVisitedUseCaseStep('form-filling') || 
                     hasVisitedUseCaseStep('results-view') ||
                     stepMapping === 'simulation-results',
          current: stepMapping === 'simulation-use-case' && currentStepInUseCase === 'segment-selection',
          // Make this clickable if we've been to segments before
          onClick: (hasVisitedUseCaseStep('segment-selection') || selectedAudienceId) ? 
            () => {
              setStepMapping('simulation-use-case');
              setCurrentStepInUseCase('segment-selection');
              setHasEditedStep(false);
            } : undefined
        },
        { 
          label: audienceData.selectedUseCase ? `3. Use Case: ${audienceData.selectedUseCase}` : '3. Select Use Case', 
          completed: hasVisitedUseCaseStep('form-filling') || 
                     hasVisitedUseCaseStep('results-view') || 
                     stepMapping === 'simulation-results',
          current: stepMapping === 'simulation-use-case' && currentStepInUseCase === 'use-case-selection',
          // Make this clickable if we've been to this step before or selected a use case
          onClick: (hasVisitedUseCaseStep('use-case-selection') || audienceData.selectedUseCase) ? 
            () => {
              setStepMapping('simulation-use-case');
              setCurrentStepInUseCase('use-case-selection');
              // If we're going back to this step from a later step, reset edit flag
              if (currentStepInUseCase === 'form-filling' || currentStepInUseCase === 'results-view') {
                setHasEditedStep(false);
              }
            } : undefined
        },
        { 
          label: '4. Complete Form', 
          completed: hasVisitedUseCaseStep('results-view') || stepMapping === 'simulation-results',
          current: stepMapping === 'simulation-use-case' && currentStepInUseCase === 'form-filling',
          // Make this clickable if we've been to this step before
          onClick: (hasVisitedUseCaseStep('form-filling') || (audienceData.selectedUseCase && formState[audienceData.selectedUseCase.toLowerCase()])) ? 
            () => {
              setStepMapping('simulation-use-case');
              setCurrentStepInUseCase('form-filling');
              // If we're going back to this step from results view, reset edit flag
              if (currentStepInUseCase === 'results-view') {
                setHasEditedStep(false);
              }
            } : undefined
        },
        { 
          label: '5. View Results', 
          completed: false, // This is always false as it's the last step
          current: stepMapping === 'simulation-results' || 
                   (stepMapping === 'simulation-use-case' && currentStepInUseCase === 'results-view'),
          // Make this clickable if we've been to this step before
          onClick: (hasVisitedUseCaseStep('results-view') || stepMapping === 'simulation-results') ? 
            () => {
              setStepMapping('simulation-results');
            } : undefined
        },
      ];
    }
    return [];
  };

  const renderCurrentStep = () => {
    switch (stepMapping) {
      case 'landing':
        return (
          <LandingScreen 
            onBuildAudience={handleBuildAudience} 
            onSelectExisting={handleSelectExisting} 
          />
        );
      case 'audience-define':
        return (
          <AudienceDefine
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'audience-questionnaire':
        return (
          <AudienceSegmentsFocus
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'audience-segments':
        return (
          <AudienceSegmentationApproach
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 'audience-final':
        return (
          <AudienceFinalize 
            onNext={handleReset}
            onBack={handleBack}
          />
        );
      case 'existing-audiences':
        return (
          <ExistingAudiences 
            onSelectAudience={handleSelectAudience}
            onBack={handleReset}
          />
        );
      case 'simulation-use-case':
        return (
          <UseCaseSelector 
            onComplete={(simulationId) => {
              setCurrentSimulationId(simulationId);
              setStepMapping('simulation-results');
              // Mark this step as visited
              setVisitedSteps(prev => new Set([...prev, 'simulation-results']));
            }}
            onBack={() => {
              setStepMapping('existing-audiences');
              setHasEditedStep(false); // Reset edit flag when going back to audiences
            }}
            audienceId={selectedAudienceId || 0}
            onStepChange={handleUseCaseStepChange}
            onEditStep={() => {
              setHasEditedStep(true);
            }}
            currentStep={currentStepInUseCase}
            hasEditedStep={hasEditedStep}
            onFormDataChange={handleFormDataChange}
          />
        );
      case 'simulation-results':
        return (
          <div className="w-full" style={{ height: "90vh", overflow: "hidden" }}>
            {currentSimulationId ? (
              <div className="h-full flex flex-col">
                <div className="mb-4 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setStepMapping('simulation-use-case');
                      setCurrentStepInUseCase('form-filling');
                    }}
                    icon={<ArrowLeft className="w-4 h-4 mr-1" />}
                  >
                    Back to form
                  </Button>
                </div>
                <div className="flex-grow overflow-hidden">
                  <SimulationResultsContent 
                    simulationId={currentSimulationId} 
                    defaultChatOpen={true}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No simulation results to display.</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    setStepMapping('simulation-use-case');
                    setCurrentStepInUseCase('segment-selection');
                  }}
                >
                  Start a new simulation
                </Button>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (!isAuthenticated) {
    return <Auth onAuthSuccess={() => {}} />;
  }

  return (
    <Layout>
      {stepMapping !== 'landing' && (
        <div className="mb-4">
          <ProgressSteps steps={getProgressSteps()} />
        </div>
      )}
      {renderCurrentStep()}
    </Layout>
  );
};

// Authentication component with navigation
const AuthRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  // If user is already authenticated, redirect to home or the previous location
  if (isAuthenticated) {
    const state = location.state as { from?: { pathname: string } };
    const from = state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }
  
  return <Auth onAuthSuccess={() => {}} />;
};

// Wrapper for SimulationResultsContent to use in routing
const SimulationResultsWrapper: React.FC = () => {
  const { simulationId } = useParams<{ simulationId: string }>();
  const navigate = useNavigate();
  
  const handleError = (error: string) => {
    console.error('Simulation error:', error);
    // Optionally navigate away or show error
  };
  
  return (
    <Layout>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden h-full">
        <SimulationResultsContent 
          simulationId={simulationId || ''} 
          onError={handleError}
          defaultChatOpen={true}
        />
      </div>
    </Layout>
  );
};

// Route wrapper component
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={
        <ProtectedRoute>
          <AppContent />
        </ProtectedRoute>
      } />
      <Route path="/build-audience" element={
        <ProtectedRoute>
          <AppContent />
        </ProtectedRoute>
      } />
      <Route path="/simulate" element={
        <ProtectedRoute>
          <AppContent />
        </ProtectedRoute>
      } />
      <Route path="/simulation-results/:simulationId" element={
        <ProtectedRoute>
          <SimulationResultsWrapper />
        </ProtectedRoute>
      } />
      <Route path="/analysis" element={
        <ProtectedRoute>
          <AnalysisPage />
        </ProtectedRoute>
      } />
      <Route path="/analysis/:simulationId" element={
        <ProtectedRoute>
          <AnalysisPage />
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />

<Route path="/chat-with-persona" element={
        <ProtectedRoute>
          <ChatWithPersona />
        </ProtectedRoute>
      } />

      {/* Ask Collective - Public pages (no auth required) */}
      <Route path="/ask-collective" element={<AskCollectiveLanding />} />
      <Route path="/ask-collective/chat" element={<AskCollectiveChat />} />

      {/* Ask Healthcare - Admin only */}
      <Route path="/ask-healthcare" element={
        <ProtectedRoute>
          <AskHealthcareChat />
        </ProtectedRoute>
      } />

      {/* Healthcare Chat - New Agent-based chat */}
      <Route path="/healthcare-chat" element={
        <ProtectedRoute>
          <HealthcareChat />
        </ProtectedRoute>
      } />

      {/* Ask Natural & Organic Shoppers - with optional session ID */}
      <Route path="/ask-natural-organic/:sessionId?" element={
        <ProtectedRoute>
          <AskNaturalOrganicChat />
        </ProtectedRoute>
      } />

      {/* Ask Reddit Personas - Admin only */}
      <Route path="/ask-reddit-personas" element={
        <ProtectedRoute>
          <AskRedditPersonasChat />
        </ProtectedRoute>
      } />
      
      {/* Subscription routes */}
      <Route path="/subscription/pricing" element={
        <ProtectedRoute>
          <PricingPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccess />
        </ProtectedRoute>
      } />
      <Route path="/subscription/cancelled" element={
        <ProtectedRoute>
          <SubscriptionCancelled />
        </ProtectedRoute>
      } />

      {/* AI Survey routes */}
      <Route path="/surveys" element={
        <ProtectedRoute>
          <SurveysList />
        </ProtectedRoute>
      } />
      <Route path="/surveys/new" element={
        <ProtectedRoute>
          <SurveyBuilder />
        </ProtectedRoute>
      } />
      <Route path="/surveys/:id" element={
        <ProtectedRoute>
          <SurveyDetails />
        </ProtectedRoute>
      } />
      <Route path="/surveys/:shareId/take" element={<SurveySession />} />

      {/* Survey Groups */}
      <Route path="/survey-groups/:groupId" element={
        <ProtectedRoute>
          <SurveyGroupDetails />
        </ProtectedRoute>
      } />

      {/* Public Simulation - No Authentication */}
      <Route path="/simulation/public/:shareToken" element={<PublicSimulationView />} />

      <Route path="/surveys/:id/analysis" element={
        <ProtectedRoute>
          <SurveyAnalysisPage />
        </ProtectedRoute>
      } />
      <Route path="/surveys/:id/responses" element={
        <ProtectedRoute>
          <SurveyResponses />
        </ProtectedRoute>
      } />
      <Route path="/surveys/sessions/:sessionId/responses" element={
        <ProtectedRoute>
          <SessionResponses />
        </ProtectedRoute>
      } />
      <Route path="/survey-complete/:sessionId" element={<SurveyComplete />} />
  
      <Route path="/auth" element={<AuthRoute />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      
      {/* Public legal routes */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />
    </Routes>
  );
};

// Main App component that provides contexts
function App() {
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  // Initialize Microsoft Clarity
  useEffect(() => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;

    // Initialize Clarity if project ID is provided
    // Skip in development unless explicitly enabled
    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        Clarity.init(clarityProjectId);
        console.log('Microsoft Clarity initialized');
      } catch (error) {
        console.warn('Failed to initialize Microsoft Clarity:', error);
      }
    } else if (isDevelopment) {
      console.log('Microsoft Clarity disabled in development mode');
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <SubscriptionProvider>
          <AudienceProvider>
            <AudienceDataProvider>
              <BuildAudienceProvider>
                <Router>
                  {maintenanceMode ? <MaintenanceWrapper /> : <AppRoutes />}
                </Router>
              </BuildAudienceProvider>
            </AudienceDataProvider>
          </AudienceProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Wrapper component to handle maintenance mode logic
const MaintenanceWrapper: React.FC = () => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
  
  useEffect(() => {
    const checkAuth = async () => {
      const authorized = await checkResetAuthorization();
      setIsAuthorized(authorized);
    };
    checkAuth();
  }, []);
  
  // Show loading while checking authorization
  if (isAuthorized === null) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }
  
  // If maintenance mode is on and user is not authorized, show maintenance page
  if (maintenanceMode && !isAuthorized) {
    return <MaintenanceMode />;
  }
  
  // If user is authorized or maintenance mode is off, show normal app
  return <AppRoutes />;
}

export default App;